// CollapsibleSidebar.js
import React, { useState, useMemo } from 'react';
import CollapsibleSection from '../CollapsibleSection';

const CollapsibleSidebar = ({
  isContainerCollapsed,
  setIsContainerCollapsed,
  isPropertyCollapsed,
  setIsPropertyCollapsed,
  selectedProperty,
  handlePropertyChange,
  properties,
  isFloorCollapsed,
  setIsFloorCollapsed,
  selectedFloor,
  handleFloorChange,
  floors,
  isPitCollapsed,
  setIsPitCollapsed,
  selectedPit,
  handlePitChange,
  pits,
  selectedTable,
  handleTableChange,
  tables,
  isFolderCollapsed,
  setIsFolderCollapsed,
  selectedFolder,
  handleFolderClick,
  folders,
  // Optional functions for the table section:
  clearTableFilter,             // function to clear the selected table
  handleTableItemContextMenu,   // function for right–click context menu
}) => {
  // ----- Pagination and Sorting Logic for the Table Section -----
  const sidebarItemsPerPage = 10;
  const [sidebarTablePage, setSidebarTablePage] = useState(1);

  // Helper function: extract the first number found in the string
  const getNumericValue = (str) => {
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  // Sort the tables numerically by their table_name
  const sortedTables = useMemo(() => {
    return [...tables].sort(
      (a, b) => getNumericValue(a.table_name) - getNumericValue(b.table_name)
    );
  }, [tables]);

  const totalSidebarPages = useMemo(
    () => Math.ceil(sortedTables.length / sidebarItemsPerPage),
    [sortedTables, sidebarItemsPerPage]
  );

  const paginatedSidebarTables = useMemo(() => {
    const startIndex = (sidebarTablePage - 1) * sidebarItemsPerPage;
    return sortedTables.slice(startIndex, startIndex + sidebarItemsPerPage);
  }, [sortedTables, sidebarTablePage, sidebarItemsPerPage]);
  // ---------------------------------------------------------------

  return (
    <div className={`collapsible-section-container ${isContainerCollapsed ? 'collapsed' : ''}`}>
      <button
        className="toggle-container-button"
        onClick={() => setIsContainerCollapsed(!isContainerCollapsed)}
        aria-label={isContainerCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
      >
        {isContainerCollapsed ? '>' : '<'}
      </button>

      {!isContainerCollapsed && (
        <div className="collapsible-sections-wrapper">
          {/* Property Section */}
          <CollapsibleSection
            title="Property"
            isOpen={!isPropertyCollapsed}
            toggleOpen={() => setIsPropertyCollapsed(!isPropertyCollapsed)}
            selectedOption={selectedProperty}
          >
            <ul className="option-list">
              {properties.map((property, index) => (
                <li
                  key={index}
                  title={property.property_name}
                  className={`option-item ${selectedProperty === property.property_name ? 'selected' : ''}`}
                  onClick={() => handlePropertyChange(property.property_name)}
                >
                  {property.property_name}
                </li>
              ))}
            </ul>
          </CollapsibleSection>

          {/* Floor Section */}
          <CollapsibleSection
            title="Floor"
            isOpen={!isFloorCollapsed}
            toggleOpen={() => setIsFloorCollapsed(!isFloorCollapsed)}
            selectedOption={selectedFloor}
          >
            <ul className="option-list">
              {floors.map((floor, index) => (
                <li
                  key={index}
                  title={floor.floor_type}
                  className={`option-item ${selectedFloor === floor.floor_type ? 'selected' : ''}`}
                  onClick={() => handleFloorChange(floor.floor_type)}
                >
                  {floor.floor_type}
                </li>
              ))}
            </ul>
          </CollapsibleSection>

          {/* Pit Section */}
          <CollapsibleSection
            title="Pit"
            isOpen={!isPitCollapsed}
            toggleOpen={() => setIsPitCollapsed(!isPitCollapsed)}
            selectedOption={selectedPit}
          >
            <ul className="option-list">
              {pits.map((pit, index) => (
                <li
                  key={index}
                  title={pit.floor_name}
                  className={`option-item ${selectedPit === pit.floor_name ? 'selected' : ''}`}
                  onClick={() => handlePitChange(pit.floor_name)}
                >
                  {pit.floor_name}
                </li>
              ))}
            </ul>
          </CollapsibleSection>

          {/* Table Section with Pagination */}
          <CollapsibleSection
            title={<span>Table</span>}
            isOpen={true} // Always visible
            toggleOpen={() => {}}
            selectedOption={selectedTable}
          >
            {selectedPit ? (
              <div className="table-dropdown-container">
                <ul className="option-list">
                  {paginatedSidebarTables.map((table, index) => (
                    <li
                      key={index}
                      title={table.table_name}
                      className={`option-item ${selectedTable === table.table_name ? 'selected' : ''}`}
                      onClick={() => handleTableChange(table)}
                      onContextMenu={
                        handleTableItemContextMenu
                          ? (e) => handleTableItemContextMenu(e, table.table_id, table.table_name)
                          : undefined
                      }
                    >
                      {table.table_name}
                    </li>
                  ))}
                </ul>

                {/* Button to clear the selected table */}
                {selectedTable && clearTableFilter && (
                  <div className="clear-table-container">
                    <button className="clear-table-button" onClick={clearTableFilter}>
                      Clear Table
                    </button>
                  </div>
                )}

                {/* Pagination controls */}
                {sortedTables.length > sidebarItemsPerPage && (
                  <div className="sidebar-pagination">
                    <button
                      onClick={() => setSidebarTablePage((prev) => Math.max(prev - 1, 1))}
                      disabled={sidebarTablePage === 1}
                    >
                      ‹
                    </button>
                    <span>
                      Page {sidebarTablePage} of {totalSidebarPages || 1}
                    </span>
                    <button
                      onClick={() => setSidebarTablePage((prev) => Math.min(prev + 1, totalSidebarPages))}
                      disabled={sidebarTablePage === totalSidebarPages}
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // If no pit is selected, render nothing inside the table section.
              null
            )}
          </CollapsibleSection>

          {/* Spacer Div to Push Folders to Bottom */}
          <div className="spacer"></div>

          {/* Folders Section */}
          {selectedTable && (
            <div className="folders-section">
              <CollapsibleSection
                title="Folders"
                isOpen={!isFolderCollapsed}
                toggleOpen={() => setIsFolderCollapsed(!isFolderCollapsed)}
                selectedOption={selectedFolder}
              >
                <div className="folders-button-container">
                  {folders.map((folder, index) => (
                    <button
                      key={index}
                      title={folder}
                      className={`folder-button ${selectedFolder === folder ? 'selected' : ''}`}
                      onClick={() => handleFolderClick(folder)}
                      aria-pressed={selectedFolder === folder}
                      aria-label={`Filter by folder ${folder}`}
                    >
                      {folder}
                    </button>
                  ))}
                  {folders.length === 0 && (
                    <p className="no-folders-message">No folders available.</p>
                  )}
                </div>
              </CollapsibleSection>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollapsibleSidebar;
